import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { key: 0 }

import { computed, ref, inject, watch } from "vue";
import modalBase from "@/components/ui/modals/modal-base.vue";
import { ModalWidth } from "@/types/ui/modals/ModalWidth";
import { DxToolbarItem } from "devextreme-vue/popup";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { ValidatedEvent } from "devextreme/ui/validator";
import SwitchToggle from "../ui/toggles/switch-toggle.vue";
import AcquaintTextbox from "@/components/ui/inputs/acquaint-textbox.vue";
import { TaskTemplatesValidation } from "acquaint-api/validation/TaskTemplatesValidation";
import { TaskTemplates } from "acquaint-api/models/TaskTemplates";
import AcquaintTextArea from "../ui/inputs/acquaint-text-area.vue";
import LookupSingleSelect from "../ui/lookup/lookup-single-select.vue";
import { LookupTableSource } from "acquaint-api/constants/LookupTableSource";


export default /*#__PURE__*/_defineComponent({
  __name: 'new-edit-task-templates-modal',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },
  /**
   * The unique identifier for the task configuration.
   * @type {number}
   * @required
   */
  configurationId: {
    type: Number,
    required: true,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
  /**
   * Emitted when an existing template configuration  has been successfully edited.
   * This notifies the parent component to refresh or update the template configuration details.
   * @property {boolean} inActive - Indicates whether the template configuration is inactive after the edit.
   */
  "configurationItemEdited",
  /**
   * Emitted when a new template configuration has been successfully created.
   * This notifies the parent component to handle the creation of a new template configuration.
   */
  "configurationItemCreated",
],
  setup(__props, { emit }) {

const props = __props;



const originalItemData = ref<TaskTemplates>();
const itemData = ref<TaskTemplates>();
const lockedByAnotherUser = ref(false);
const lockDescriptionField = ref(false);
const keepingModalOpening = ref(false);
const acquaintApiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const submissionResponse = ref<TaskTemplates>();
const displayCancelConfirmationModal = ref(false);



const showModal = computed({
  get: () => props.modelValue,
  set: (value: Boolean) => emit("update:modelValue", value),
});

const lockConfigurationItem = async () => {
  try {
    await acquaintApiClient.httpPostVoid(
      `database/tasktemplates/${props.configurationId}/lock`,
      { lock: true, extraInfo: "" },
    );
  } catch (err) {
    lockedByAnotherUser.value = true;
    lockDescriptionField.value = true;
    displayErrorMessage(
      "These details are already being edited by another user.",
    );
  }
};

const unLockConfigurationItem = async () => {
  try {
    await acquaintApiClient.httpPostVoid(
      `database/tasktemplates/${props.configurationId}/lock`,
      { lock: false, extraInfo: "" },
    );
  } catch (err) {
    displayErrorMessage("We were unable to unlock these details.");
  }
};

const fetchItemData = async () => {
  try {
    if (props.configurationId == 0) {
      originalItemData.value = await acquaintApiClient.httpGet<TaskTemplates>(
        `database/tasktemplates/defaultmodel`,
      );
    } else {
      originalItemData.value = await acquaintApiClient.httpGet<TaskTemplates>(
        `database/tasktemplates/${props.configurationId}`,
      );
    }
  } catch (err) {
    originalItemData.value = undefined;
    itemData.value = undefined;
    if (props.configurationId == 0) {
      displayErrorMessage("The selected details could not be fetched");
    } else {
      displayErrorMessage("We could not fetch the default data");
    }
  }
};

watch(
  () => itemData.value,
  () => {
    displayCancelConfirmationModal.value =
      JSON.stringify(originalItemData.value) != JSON.stringify(itemData.value);
  },
  { deep: true },
);

watch(
  () => showModal.value,
  async () => {
    if (keepingModalOpening.value) {
      return;
    }

    if (showModal.value) {
      lockedByAnotherUser.value = false;
      lockDescriptionField.value = false;
      displayCancelConfirmationModal.value = false;

      await fetchItemData();
      if (originalItemData.value) {
        itemData.value = JSON.parse(JSON.stringify(originalItemData.value));
      }

      if (props.configurationId == 0) {
        return;
      }

      if (props.configurationId < 1000) {
        lockDescriptionField.value = true;
        displayErrorMessage(
          "You cannot fully edit this system record's description as Acquaint created and maintains it for data integrity purposes.",
        );
        return;
      }

      await lockConfigurationItem();
    }
  },
);

const saveDetails = async (e: any) => {
  const validation: ValidatedEvent = e.validationGroup.validate();
  // Don't continue if user has entered invalid data
  if (!validation.isValid) {
    return;
  }

  if (!(await callSaveApi())) {
    return;
  }

  lockedByAnotherUser.value = true;
  displayCancelConfirmationModal.value = false;

  if (props.configurationId > 0) {
    await unLockConfigurationItem();
    emit("configurationItemEdited", itemData.value?.inActive);
  } else {
    emit("configurationItemCreated");
  }

  showModal.value = false;
};

const callSaveApi = async () => {
  try {
    if (props.configurationId == 0) {
      submissionResponse.value =
        await acquaintApiClient.httpPost<TaskTemplates>(
          `database/tasktemplates`,
          itemData.value,
        );
    } else {
      submissionResponse.value = await acquaintApiClient.httpPut<TaskTemplates>(
        `database/tasktemplates/${props.configurationId}`,
        itemData.value,
      );
    }
    return true;
  } catch (err) {
    if (props.configurationId == 0) {
      displayErrorMessage("A new system item could not be created.");
    } else {
      displayErrorMessage("The selected details could not be edited.");
    }
    return false;
  }
};
const cancelClick = () => {
  showModal.value = false;
};

const keepEnteredInformation = () => {
  keepingModalOpening.value = true;
};

const cancelPromptDenied = () => {
  keepingModalOpening.value = true;
};

const modalClosed = async () => {
  if (props.configurationId > 0) {
    await unLockConfigurationItem();
  }
  keepingModalOpening.value = false;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(modalBase, {
    title: "Task Templates",
    modelValue: showModal.value,
    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showModal).value = $event)),
    showCloseButton: true,
    showCloseConfirmationPrompt: displayCancelConfirmationModal.value,
    height: "515px",
    onCancelConfirmationModal: keepEnteredInformation,
    onModalClosed: modalClosed,
    width: _unref(ModalWidth).XSmall,
    onCancelPromptDenied: cancelPromptDenied
  }, {
    content: _withCtx(() => [
      (itemData.value)
        ? (_openBlock(), _createElementBlock("section", _hoisted_1, [
            _createVNode(AcquaintTextbox, {
              fieldValidation: _unref(TaskTemplatesValidation).Description,
              label: "Description",
              modelValue: itemData.value.description,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((itemData.value.description) = $event)),
              readOnly: lockDescriptionField.value,
              validationGroup: "TaskTemplatesValidationGroup"
            }, null, 8, ["fieldValidation", "modelValue", "readOnly"]),
            _createVNode(AcquaintTextbox, {
              fieldValidation: _unref(TaskTemplatesValidation).Subject,
              label: "Subject",
              modelValue: itemData.value.subject,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((itemData.value.subject) = $event)),
              readOnly: lockedByAnotherUser.value,
              validationGroup: "TaskTemplatesValidationGroup"
            }, null, 8, ["fieldValidation", "modelValue", "readOnly"]),
            _createVNode(AcquaintTextArea, {
              fieldValidation: _unref(TaskTemplatesValidation).Body,
              label: "Body",
              modelValue: itemData.value.body,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((itemData.value.body) = $event)),
              readOnly: lockedByAnotherUser.value,
              validationGroup: "TaskTemplatesValidationGroup"
            }, null, 8, ["fieldValidation", "modelValue", "readOnly"]),
            _createVNode(LookupSingleSelect, {
              "lookup-source": _unref(LookupTableSource).TaskTypes,
              fieldValidation: _unref(TaskTemplatesValidation).TaskTypeID,
              label: "Type",
              modelValue: itemData.value.taskTypeID,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((itemData.value.taskTypeID) = $event)),
              readOnly: lockedByAnotherUser.value,
              validationGroup: "TaskTemplatesValidationGroup"
            }, null, 8, ["lookup-source", "fieldValidation", "modelValue", "readOnly"]),
            _createVNode(SwitchToggle, {
              label: "Inactive",
              labelWidth: "7.5rem",
              modelValue: itemData.value.inActive,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((itemData.value.inActive) = $event)),
              readOnly: lockedByAnotherUser.value
            }, null, 8, ["modelValue", "readOnly"])
          ]))
        : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      _createVNode(_unref(DxToolbarItem), {
        widget: "dxButton",
        toolbar: "bottom",
        location: "after",
        options: {
          text: 'Save',
          disabled: lockedByAnotherUser.value,
          onClick: (e) => {
            saveDetails(e);
          },
          validationGroup: 'TaskTemplatesValidationGroup',
        }
      }, null, 8, ["options"]),
      _createVNode(_unref(DxToolbarItem), {
        widget: "dxButton",
        toolbar: "bottom",
        location: "after",
        options: {
          text: 'Cancel',
          onClick: () => {
            cancelClick();
          },
        }
      }, null, 8, ["options"])
    ]),
    _: 1
  }, 8, ["modelValue", "showCloseConfirmationPrompt", "width"]))
}
}

})